import parse, { NationalNumber, isValidPhoneNumber } from "libphonenumber-js";
import sortBy from "lodash/sortBy";

import {
  callingCodes,
  primaryCallingCodes,
  secondaryCallingCodes,
} from "./consts/CALLING_CODES";
import { StrictLocalizeFunction } from "./localization";

export interface ICountryCodeOption {
  label: string;
  countryCallingCode: string;
  format?: string;
}

export namespace ICountryCodeOption {
  export const build = (
    partial?: Partial<ICountryCodeOption>
  ): ICountryCodeOption => ({
    label: "",
    countryCallingCode: "",
    ...partial,
  });
}

const getPhoneFormat = (country: string) => {
  switch (country) {
    case "United States of America":
    case "Canada":
      return "(###)-###-####";
    default:
      return undefined;
  }
};

const primaryOptions = (
  strictLocalize: StrictLocalizeFunction
): ICountryCodeOption[] =>
  primaryCallingCodes(strictLocalize)
    .concat(sortBy(secondaryCallingCodes(strictLocalize), "[0]"))
    .map(([label, countryCallingCode]) => ({
      label: label ?? "",
      countryCallingCode: countryCallingCode ?? "",

      format: getPhoneFormat(label ?? ""),
    }));

const allOptions = (
  strictLocalize: StrictLocalizeFunction
): ICountryCodeOption[] =>
  callingCodes(strictLocalize).map(([label, countryCallingCode]) => ({
    label: label ?? "",
    countryCallingCode: countryCallingCode ?? "",
  }));

export const countryCallingCodeOptions = (
  strictLocalize: StrictLocalizeFunction
): ICountryCodeOption[] =>
  primaryOptions(strictLocalize).concat(
    sortBy(allOptions(strictLocalize), "label")
  );

interface IParsedNumber {
  countryCallingCodeOption: ICountryCodeOption;
  nationalPhoneNumber: NationalNumber;
}

export const validatePhoneNumber = (phoneNumber?: string): boolean => {
  return phoneNumber ? isValidPhoneNumber(phoneNumber) : false;
};

export const parsePhoneNumber = (
  phoneNumber: string | undefined,
  countryCodeOption: ICountryCodeOption | undefined,
  strictLocalize: StrictLocalizeFunction
): IParsedNumber => {
  const options = countryCallingCodeOptions(strictLocalize);

  // Special-case for empty/default input.
  if (phoneNumber?.trim() === "+1") {
    return {
      countryCallingCodeOption: options[0] ?? ICountryCodeOption.build(), // USA
      nationalPhoneNumber: "",
    };
  }

  const parsed = parse(phoneNumber ?? "");
  if (!parsed?.isPossible()) {
    return {
      countryCallingCodeOption:
        options.find(
          (c) => c.countryCallingCode === `+${parsed?.countryCallingCode}`
        ) ??
        countryCodeOption ??
        options[0] ??
        ICountryCodeOption.build(), // USA
      nationalPhoneNumber: parsed?.nationalNumber ?? phoneNumber ?? "",
    };
  }

  const countryCallingCodeOption = options.find(
    (option) => `+${parsed.countryCallingCode}` === option.countryCallingCode
  );
  if (!countryCallingCodeOption) {
    return {
      countryCallingCodeOption: options[0] ?? ICountryCodeOption.build(), // USA
      nationalPhoneNumber: parsed.nationalNumber,
    };
  }
  return {
    countryCallingCodeOption,
    nationalPhoneNumber: parsed.nationalNumber,
  };
};
